import { AfterViewInit, Component, ElementRef, HostListener, inject, Input, OnInit, ViewChild } from '@angular/core';
import { BaseComponent } from 'src/app/core/domain/base-component';
import { CvfState } from 'src/app/core/data-models/survey-modes';
import { GlobalsService } from 'src/app/core/services/globals.service';
import { LanguageService } from 'src/app/core/services/language.service';
import { ModuleName } from 'src/app/shared/enums/ModuleName';
import { ZenSnackbarService } from 'src/app/shared/Theme/services/zen-snackbar.service';
import { CvfAllocationState, CvfAnswerService } from './cvf-answer.service';

@Component({
  selector: 'constant-sum-cvf',
  templateUrl: './constant-sum-cvf.component.html',
  styleUrls: ['./constant-sum-cvf.component.scss']
})
export class ConstantSumCvfComponent extends BaseComponent implements OnInit, AfterViewInit {
  private cvfAnswerService = inject(CvfAnswerService);

  /** The totals row floats above the nav bar until its slot scrolls into view; the slot keeps its height meanwhile. */
  @ViewChild('totalsSlot') totalsSlot: ElementRef<HTMLElement>;
  floating = false;
  totalsHeight = 0;
  navOffset = 0;

  /** Amount the +/- buttons nudge an allocation by. */
  readonly cvfStep = 5;

  lgCount;
  question;
  @Input('question') set q(question: any) {
    this.question = question;
    this.initAnswers();
  }
  @Input() language;

  /**
   * Which allocation columns to render. 'both' is the one-shot layout; 'now' and
   * 'future' are the two-batch passes. This only affects what is displayed --
   * initAnswers always seeds both maps so the persisted shape never varies.
   */
  @Input() state: CvfState = 'both';

  ModuleNames = ModuleName;

  constructor(private _globals: GlobalsService, private languageService: LanguageService, private snackbar: ZenSnackbarService) {
    super();
    this.lgCount = _globals.lgCount;
  }

  ngOnInit(): void {
    this.initAnswers();
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.placeTotals(), 300);
  }

  @HostListener('window:scroll')
  @HostListener('window:resize')
  placeTotals() {
    const slot = this.totalsSlot?.nativeElement;
    if (!slot) return;
    if (!this.floating) this.totalsHeight = (slot.firstElementChild as HTMLElement).offsetHeight;
    // stay above the taker's nav bar while it is stuck to the bottom of the screen
    const navTop = document.querySelector('.stickyFooter')?.getBoundingClientRect().top ?? window.innerHeight;
    const fold = Math.min(navTop, window.innerHeight);
    this.navOffset = window.innerHeight - fold;
    this.floating = slot.getBoundingClientRect().bottom > fold;
  }

  get cvfTotal(): number {
    return this.cvfAnswerService.total;
  }

  get showNow(): boolean {
    return this.state !== 'future';
  }

  get showFuture(): boolean {
    return this.state !== 'now';
  }

  /** Both columns visible means the statement column has to give up room -- only at md: and up; mobile always stacks full-width. */
  get statementWidthClass(): string {
    return this.showNow && this.showFuture ? 'md:w-8/12' : 'md:w-10/12';
  }

  get nowTotal(): number {
    return this.cvfAnswerService.stateTotal(this.question?.answer, 'now');
  }

  get futureTotal(): number {
    return this.cvfAnswerService.stateTotal(this.question?.answer, 'future');
  }

  initAnswers() {
    this.cvfAnswerService.ensureShape(this.question);
  }

  extractNameLanguage(eG: any) {
    return this.languageService.extractNameLanguage(eG, this.language);
  }

  onInputChange(state: CvfAllocationState, optionKey: string, value: number | null, inputEl?: HTMLInputElement) {
    const parsed = Number.isFinite(value) ? Math.trunc(value) : 0;

    if (Number.isFinite(value) && (value < 0 || value > this.cvfTotal))
      this.snackbar.error(this.localize('txt_cvf_range_error', this.language, this.ModuleNames.Culture));

    if (this.question?.answer?.[state]) {
      const clamped = this.cvfAnswerService.clampCellValue(this.question.answer, state, optionKey, parsed);
      this.question.answer[state][optionKey] = clamped;
      if (inputEl && (inputEl.validity?.badInput || (inputEl.value !== '' && inputEl.value !== String(clamped))))
        inputEl.value = String(clamped);
    }
  }

  /** type="number" still accepts sign/exponent/decimal keystrokes; allocations are whole 0-100 points. */
  blockInvalidKeys(event: KeyboardEvent) {
    if (['-', '+', 'e', 'E', '.', ','].includes(event.key))
      event.preventDefault();
  }

  adjustValue(state: CvfAllocationState, optionKey: string, delta: number) {
    if (!this.question?.answer?.[state]) return;
    const current = this.question.answer[state][optionKey] || 0;
    this.question.answer[state][optionKey] = this.cvfAnswerService.clampCellValue(this.question.answer, state, optionKey, current + delta);
  }

  /**
   * Wheel over the −/input/+ cluster: same ±cvfStep + clamp as the buttons.
   * Blocks page scroll while the pointer is over the cluster.
   */
  onClusterWheel(event: WheelEvent, state: CvfAllocationState, optionKey: string) {
    event.preventDefault();
    if (event.deltaY === 0) return;
    this.adjustValue(state, optionKey, event.deltaY < 0 ? this.cvfStep : -this.cvfStep);
  }

  valueOf(state: CvfAllocationState, optionKey: string): number {
    return this.question?.answer?.[state]?.[optionKey] || 0;
  }

  isAtMin(state: CvfAllocationState, optionKey: string): boolean {
    return this.valueOf(state, optionKey) <= 0;
  }

  /** Whether this state's total already leaves no room for another increment. Column-wide, not per-cell. */
  isAtMax(state: CvfAllocationState): boolean {
    return this.cvfAnswerService.remainingBudget(this.question?.answer, state) <= 0;
  }
}
